import {
  Col,
  Row,
  Timeline,
  Image,
  Carousel,
  Modal,
  Spin,
  Button,
  Space,
  message,
} from 'antd';
import Upload from './Upload';
import { useRequest } from 'ahooks';
import { useCompany } from '../../hooks/company';
import { companyMediaController, oSSController } from '../../api';
import { PageHeader } from '@ant-design/pro-components';
import {
  createContext,
  PropsWithChildren,
  useContext,
  useMemo,
  useState,
} from 'react';
import ImageList from './ImageList';
import { DeleteOutline } from 'antd-mobile-icons';

const refreshContext = createContext<{ refresh: () => void }>({
  refresh() {},
});

const ImagePreview = ({
  ossKey,
  onClick,
  width,
}: {
  ossKey: string;
  onClick: () => void;
  width?: number;
}) => {
  const { id: companyId } = useCompany();

  const { data: url } = useRequest(
    () => {
      return oSSController.getOSSUrl(companyId, ossKey);
    },
    {
      refreshDeps: [companyId, ossKey],
    },
  );

  return <Image src={url} width={width} preview={false} onClick={onClick} />;
};

const DeleteCategory = ({ category }: { category: string }) => {
  const { id: companyId } = useCompany();

  const { refresh } = useContext(refreshContext);

  const { run: deleteCategory, loading } = useRequest(
    () => {
      return companyMediaController.deleteMediaByCategory(companyId, category);
    },
    {
      manual: true,
      onError: (e) => {
        message.error(e.message);
      },
      onSuccess: () => {
        message.success('删除成功');
        refresh();
      },
    },
  );

  return (
    <Button
      onClick={() => deleteCategory()}
      loading={loading}
      icon={<DeleteOutline />}
      danger
      size="small"
    />
  );
};

const DeleteWrapper = ({
  children,
  category,
}: PropsWithChildren<{ category: string }>) => {
  const { hasPermission } = useCompany();

  const canEdit = useMemo(() => {
    return hasPermission('page:media:editor');
  }, [hasPermission]);

  if (!canEdit) {
    return <>{children}</>;
  }

  return (
    <>
      <Space>
        {children}
        <DeleteCategory category={category} />
      </Space>
    </>
  );
};

export default () => {
  const { id: companyId } = useCompany();

  const [currentCategory, setCurrentCategory] = useState<string>();

  const { data, loading, refresh } = useRequest(
    () => {
      return companyMediaController.listCategoryV2(companyId);
    },
    {
      refreshDeps: [companyId],
    },
  );

  return (
    <>
      <PageHeader extra={[<Upload key="upload" />]}>
        <Spin spinning={loading}>
          <Row>
            <Col span={currentCategory ? 4 : 24}>
              <refreshContext.Provider value={{ refresh }}>
                <Timeline mode="alternate">
                  {data?.map((item) => {
                    return (
                      <Timeline.Item
                        color={
                          currentCategory === item.category ? 'blue' : 'gray'
                        }
                        key={item.category}
                        label={
                          <DeleteWrapper category={item.category}>
                            {item.category}
                          </DeleteWrapper>
                        }
                        position="right"
                      >
                        <ImagePreview
                          ossKey={item.ossKey}
                          width={currentCategory ? undefined : 400}
                          onClick={() => setCurrentCategory(item.category)}
                        />
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              </refreshContext.Provider>
            </Col>
            {currentCategory && (
              <Col span={20}>
                <ImageList companyId={companyId} category={currentCategory} />
              </Col>
            )}
          </Row>
        </Spin>
      </PageHeader>
    </>
  );
};
